import { BooleanInput, Button, Container, Element, Label, SelectInput } from '@playcanvas/pcui';

import { Events } from '../events';
import { Splat } from '../splat';
import { i18n } from './localization';
import { AlignApplyResult, AlignCancelResult, AlignIsolation, AlignState } from '../tools/align-tool';
import alignIcon from './svg/align.svg';

// unusual scale factors are usually a mis-clicked pair, not reality: warn,
// but don't block (drone vs portal-cam captures genuinely differ in scale)
const SCALE_WARN_MIN = 0.2;
const SCALE_WARN_MAX = 5;

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

const formatMeters = (value: number) => {
    return value < 1 ? `${(value * 100).toFixed(1)} cm` : `${value.toFixed(2)} m`;
};

// non-modal, right-docked dialog driving the align tool: pick the two
// models, click landmark pairs in the scene, apply the solved similarity
// transform. all scene state lives in the tool (src/tools/align-tool.ts);
// this dialog renders its state and forwards commands.
class AlignDialog extends Container {
    show: () => void;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'align-dialog',
            class: ['settings-dialog', 'blocks-shortcuts'],
            hidden: true,
            tabIndex: -1
        };

        super(args);

        const dialog = new Container({
            id: 'dialog'
        });

        // header

        const headerIcon = createSvg(alignIcon, { id: 'icon' });
        const headerText = new Label({ id: 'text' });
        i18n.bindText(headerText, () => i18n.t('popup.align.header').toUpperCase());
        const header = new Container({ id: 'header' });
        header.append(headerIcon);
        header.append(headerText);

        const makeRow = (localeKey: string, input: Element) => {
            const label = new Label({ class: 'label' });
            i18n.bindText(label, localeKey);
            const row = new Container({ class: 'row' });
            row.append(label);
            row.append(input);
            return row;
        };

        // model selection

        let splats: Splat[] = [];

        const refSelect = new SelectInput({ class: 'select' });
        const refRow = makeRow('align.reference', refSelect);

        const movSelect = new SelectInput({ class: 'select' });
        const movRow = makeRow('align.movable', movSelect);

        const swapButton = new Button({ id: 'align-swap-button', enabled: false });
        i18n.bindText(swapButton, 'align.swap');
        const swapRow = new Container({ class: 'row', id: 'align-swap-row' });
        swapRow.append(swapButton);

        const withScaleInput = new BooleanInput({ class: 'boolean', value: true });
        const withScaleRow = makeRow('align.with-scale', withScaleInput);

        const isolationSelect = new SelectInput({ class: 'select', defaultValue: 'dim' });
        i18n.bindOptions(isolationSelect, () => [
            { v: 'normal', t: i18n.t('align.isolation-normal') },
            { v: 'dim', t: i18n.t('align.isolation-dim') },
            { v: 'hide', t: i18n.t('align.isolation-hide') }
        ]);
        const isolationRow = makeRow('align.isolation', isolationSelect);

        // status hint (which click is expected next / validation issues)

        const statusLabel = new Label({ id: 'align-status-label' });

        // pair list

        const pairsHeaderRow = new Container({ id: 'align-pairs-header-row' });
        const pairsHeader = new Label({ id: 'align-pairs-header' });
        i18n.bindText(pairsHeader, 'align.pairs');
        const clearButton = new Button({ id: 'align-pairs-clear' });
        i18n.bindText(clearButton, 'align.clear');
        pairsHeaderRow.append(pairsHeader);
        pairsHeaderRow.append(clearButton);

        const pairList = new Container({ id: 'align-pair-list' });

        // results

        const rmseLabel = new Label({ id: 'align-rmse-label', hidden: true });
        const warnLabel = new Label({ id: 'align-warn-label', hidden: true });

        // content

        const content = new Container({ id: 'content' });
        content.append(refRow);
        content.append(movRow);
        content.append(swapRow);
        content.append(withScaleRow);
        content.append(isolationRow);
        content.append(statusLabel);
        content.append(pairsHeaderRow);
        content.append(pairList);
        content.append(rmseLabel);
        content.append(warnLabel);

        // footer

        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({ class: 'button' });
        i18n.bindText(cancelButton, 'popup.cancel');

        const applyButton = new Button({ class: 'button', enabled: false });
        i18n.bindText(applyButton, 'align.apply');

        const doneButton = new Button({ class: 'button' });
        i18n.bindText(doneButton, 'align.done');

        footer.append(cancelButton);
        footer.append(applyButton);
        footer.append(doneButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);
        this.append(dialog);

        // model select plumbing

        let suppressSelect = 0;

        const fireModels = () => {
            if (suppressSelect) {
                return;
            }
            events.fire(
                'align.setModels',
                splats[parseInt(refSelect.value, 10)] ?? null,
                splats[parseInt(movSelect.value, 10)] ?? null
            );
        };

        // (re)populate the selects from the scene, keeping the current
        // choices where the splats still exist
        const refreshModels = () => {
            const prevRef = splats[parseInt(refSelect.value, 10)];
            const prevMov = splats[parseInt(movSelect.value, 10)];
            splats = (events.invoke('scene.splats') as Splat[] | undefined)?.slice() ?? [];

            const options = splats.map((splat, i) => ({ v: String(i), t: splat.name }));
            suppressSelect++;
            refSelect.options = options;
            movSelect.options = options;
            const refIndex = splats.indexOf(prevRef);
            const movIndex = splats.indexOf(prevMov);
            // defaults: first model stays fixed, the newest one moves
            refSelect.value = String(refIndex >= 0 ? refIndex : 0);
            movSelect.value = String(movIndex >= 0 ? movIndex : splats.length - 1);
            suppressSelect--;

            fireModels();
        };

        refSelect.on('change', fireModels);
        movSelect.on('change', fireModels);

        events.on('scene.elementAdded', () => {
            if (!this.hidden) {
                refreshModels();
            }
        });
        events.on('scene.elementRemoved', () => {
            if (!this.hidden) {
                refreshModels();
            }
        });

        // render the tool state

        let statusTimeout = 0;
        let suppressIsolation = 0;

        const render = (state: AlignState) => {
            if (this.hidden) {
                return;
            }

            window.clearTimeout(statusTimeout);
            statusLabel.dom.style.color = '';
            if (state.sameModels) {
                statusLabel.text = i18n.t('align.same-model');
                statusLabel.dom.style.color = '#fa0';
            } else if (!state.hasModels) {
                statusLabel.text = '';
            } else if (state.phase === 'mov') {
                statusLabel.text = i18n.t('align.hint-pick-mov');
            } else {
                statusLabel.text = i18n.t('align.hint-pick-ref', { num: String(state.pairs.length + 1) });
            }

            swapButton.enabled = state.hasModels && !state.sameModels;

            suppressIsolation++;
            isolationSelect.value = state.isolation;
            suppressIsolation--;

            pairList.clear();
            state.pairs.forEach((pair, i) => {
                const isOutlier = i === state.outlierIndex;
                const row = new Container({ class: isOutlier ? ['align-pair-row', 'align-pair-row-outlier'] : 'align-pair-row' });
                const name = new Label({ class: 'align-pair-name', text: `${i + 1}` });

                const isRefSelected = !!(state.selection && state.selection.pairIndex === i && state.selection.side === 'ref');
                const refChip = new Button({
                    class: [
                        'align-pair-chip',
                        ...(pair.hasRef ? [] : ['align-pair-chip-empty']),
                        ...(isRefSelected ? ['align-pair-chip-selected'] : [])
                    ],
                    enabled: pair.hasRef
                });
                i18n.bindText(refChip, 'align.chip-ref');
                refChip.dom.title = i18n.t('align.point-ref');
                refChip.on('click', () => events.fire('align.selectPoint', i, 'ref'));

                const isMovSelected = !!(state.selection && state.selection.pairIndex === i && state.selection.side === 'mov');
                const movChip = new Button({
                    class: [
                        'align-pair-chip',
                        ...(pair.hasMov ? [] : ['align-pair-chip-empty']),
                        ...(isMovSelected ? ['align-pair-chip-selected'] : [])
                    ],
                    enabled: pair.hasMov
                });
                i18n.bindText(movChip, 'align.chip-mov');
                movChip.dom.title = i18n.t('align.point-mov');
                movChip.on('click', () => events.fire('align.selectPoint', i, 'mov'));

                const residual = new Label({
                    class: 'align-pair-residual',
                    text: pair.complete ? (pair.residual !== null ? formatMeters(pair.residual) : '…') : '…'
                });
                const deleteButton = new Button({ class: 'align-pair-delete', text: '✕' });
                deleteButton.dom.title = i18n.t('align.delete-pair');
                deleteButton.on('click', () => events.fire('align.deletePair', i));

                row.append(name);
                row.append(refChip);
                row.append(movChip);
                row.append(residual);
                row.append(deleteButton);
                pairList.append(row);
            });

            rmseLabel.hidden = state.rmse === null;
            if (state.rmse !== null) {
                rmseLabel.text = state.completeCount === 3 ?
                    `${i18n.t('align.rms', { value: formatMeters(state.rmse) })} · ${i18n.t('align.hint-few-pairs')}` :
                    i18n.t('align.rms', { value: formatMeters(state.rmse) });
            }

            applyButton.enabled = state.hasModels && !state.sameModels && state.completeCount >= 3;
        };

        events.on('align.changed', (state: AlignState) => render(state));

        // transient wrong-model hints override the phase hint briefly
        events.on('align.status', (status: 'wrong-ref' | 'wrong-mov') => {
            if (this.hidden) {
                return;
            }
            statusLabel.text = i18n.t(status === 'wrong-ref' ? 'align.hint-wrong-ref' : 'align.hint-wrong-mov');
            statusLabel.dom.style.color = '#fa0';
            window.clearTimeout(statusTimeout);
            statusTimeout = window.setTimeout(() => render(events.invoke('align.state')), 1500);
        });

        // commands

        const apply = () => {
            if (!applyButton.enabled) {
                return;
            }
            warnLabel.hidden = true;
            const result = events.invoke('align.apply') as AlignApplyResult;
            if (!result.ok) {
                if (result.error === 'degenerate') {
                    warnLabel.text = i18n.t('align.error-degenerate');
                    warnLabel.hidden = false;
                }
                return;
            }
            if (result.scale !== undefined && (result.scale < SCALE_WARN_MIN || result.scale > SCALE_WARN_MAX)) {
                warnLabel.text = i18n.t('align.warn-scale', { value: result.scale.toFixed(2) });
                warnLabel.hidden = false;
            }
        };

        // reverts this session's applied transform(s) if nothing else was
        // edited since, then closes. if a revert was needed but unsafe (the
        // user edited something else in between), stays open with a warning
        // instead of silently closing over an untouched-looking alignment.
        const cancel = () => {
            const result = events.invoke('align.cancel') as AlignCancelResult;
            if (result.attempted && !result.reverted) {
                warnLabel.text = i18n.t('align.warn-cancel-blocked');
                warnLabel.hidden = false;
                return;
            }
            this.hide();
        };

        applyButton.on('click', apply);
        cancelButton.on('click', cancel);
        doneButton.on('click', () => this.hide());

        clearButton.on('click', () => {
            warnLabel.hidden = true;
            events.fire('align.reset');
        });

        swapButton.on('click', () => {
            events.fire('align.swap');
        });

        withScaleInput.on('change', (value: boolean) => {
            events.fire('align.setWithScale', value);
        });

        isolationSelect.on('change', (value: AlignIsolation) => {
            if (suppressIsolation) {
                return;
            }
            events.fire('align.setIsolation', value);
        });

        const keydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                // step back one thing at a time (deselect a point, then
                // discard a pending pair) before falling through to a full
                // cancel — mirrors the footer's staged undo
                const stepped = events.invoke('align.stepBack') as boolean;
                if (!stepped) {
                    cancel();
                }
            } else if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                apply();
            }
        };

        this.show = () => {
            if (!this.hidden) {
                return;
            }
            this.hidden = false;
            warnLabel.hidden = true;
            rmseLabel.hidden = true;

            // the tool owns the session; activate it first, then hand it the
            // models so the initial state broadcast reaches us
            if (events.invoke('tool.active') !== 'align') {
                events.fire('tool.align');
            }
            events.fire('align.setWithScale', withScaleInput.value);
            refreshModels();
            render(events.invoke('align.state'));

            document.addEventListener('keydown', keydown);
            this.dom.focus();
        };

        this.hide = () => {
            if (this.hidden) {
                return;
            }
            this.hidden = true;
            document.removeEventListener('keydown', keydown);
            if (events.invoke('tool.active') === 'align') {
                events.fire('tool.deactivate');
            }
        };

        // the tool can also be deactivated from outside (another tool was
        // picked, tab switch): the dialog must not linger as a dead shell
        events.on('tool.align.deactivated', () => {
            this.hide();
        });

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { AlignDialog };
